/**
 * Weekly student leaderboard — XP earned in the last 7 days via correct observations + report grades.
 * Fetches from /api/leaderboard/weekly with optional ?class_code=CODE filter.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Zap, Loader2, Crown } from "lucide-react";

const medal = (rank) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
};

export const Leaderboard = ({ classCode = null }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = classCode ? { class_code: classCode } : {};
        const res = await api.get("/leaderboard/weekly", { params });
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.warn("leaderboard load failed:", err?.message || err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [classCode]);

  if (loading) {
    return (
      <div className="rounded-3xl bg-slate-900/60 border border-slate-800 p-6 flex items-center gap-2 text-slate-400">
        <Loader2 className="animate-spin h-4 w-4" /> Loading leaderboard…
      </div>
    );
  }

  const top = data?.top || [];
  const me = data?.me;

  return (
    <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border-2 border-amber-500/30 p-6 shadow-xl" data-testid="leaderboard-card">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-amber-400" />
        <h3 className="font-outfit font-black text-xl text-slate-100">Weekly Leaderboard</h3>
        <span className="ml-auto text-[10px] text-amber-300 font-mono uppercase tracking-widest bg-amber-500/10 px-2 py-0.5 rounded-full">
          Last 7 days
        </span>
      </div>

      {top.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <Zap className="mx-auto h-8 w-8 text-slate-600 mb-2" />
          <div className="text-sm">No XP earned yet this week.</div>
          <div className="text-xs mt-1 text-slate-500">Complete a guided experiment to appear on the board!</div>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {top.map((r) => {
            const isMe = me && r.user_id === me.user_id;
            return (
              <li
                key={r.user_id}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                  isMe
                    ? "bg-cyan-500/15 border border-cyan-500/40"
                    : r.rank <= 3
                    ? "bg-amber-500/5"
                    : ""
                }`}
                data-testid={`leader-row-${r.rank}`}
              >
                <div className={`w-8 text-center font-mono text-sm ${r.rank <= 3 ? "text-amber-400" : "text-slate-500"}`}>
                  {medal(r.rank)}
                </div>
                <div className="flex-1 truncate">
                  <div className={`font-bold text-sm ${isMe ? "text-cyan-300" : "text-slate-100"}`}>
                    {r.user_name}{isMe && <span className="ml-2 text-[10px] text-cyan-400 font-mono uppercase">You</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-amber-300 font-mono font-bold">
                  <Zap className="h-3.5 w-3.5" />
                  {r.xp}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* My position (if outside top-20) */}
      {me && !top.some((r) => r.user_id === me.user_id) && (
        <div className="mt-3 pt-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-cyan-500/15 border border-cyan-500/40" data-testid="leader-me">
            <Crown className="h-4 w-4 text-cyan-300" />
            <div className="flex-1 text-sm text-cyan-300 font-bold truncate">
              {me.user_name} <span className="text-[10px] font-mono uppercase ml-2 text-cyan-400">You</span>
            </div>
            <div className="flex items-center gap-1 text-amber-300 font-mono font-bold">
              <Zap className="h-3.5 w-3.5" />
              {me.xp}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 text-[10px] text-slate-500 font-mono uppercase tracking-widest text-center">
        Earn XP · +10 per correct observation · up to +50 per lab report
      </div>
    </div>
  );
};

export default Leaderboard;
